import { Directive, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import {
  RtFormLabelsFromForm,
  RtFormPlaceholdersFromForm,
  RtValidationMessagesFromForm,
  RtValidatorsShared
} from 'rt-inputs';
import { Subscription } from 'rxjs';
import { AUTH_METHOD } from 'symbols';
import { Registration, RegistrationFail, RegistrationSuccess } from '../states/gp-auth/gp-auth.actions';
import { GpAuthState } from '../states/gp-auth/gp-auth.state';
import { GpAuthConfigModel, RegistrationData } from '../symbols';

@Directive({
  standalone: true
})
export abstract class RegistrationDirective implements OnInit, OnDestroy {

  public readonly isAuthenticated$ = this.store.select(GpAuthState.isAuthenticated);
  public readonly authStatus$ = this.store.select(GpAuthState.loginStatus);


  public readonly subscription = new Subscription();
  public readonly authMethod = AUTH_METHOD;

  public form = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(200)]),
    password: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    confirmPassword: new FormControl('', [Validators.required, Validators.maxLength(200), RtValidatorsShared.matchOtherValidator('password')]),
    firstName: new FormControl('', [Validators.required, Validators.maxLength(200)]),
    lastName: new FormControl('', [Validators.required, Validators.maxLength(200)])
  });


  public labels: RtFormLabelsFromForm<typeof this.form> = {
    username: `User name`,
    email: `Email`,
    password: `Password`,
    firstName: `First name`,
    lastName: `Last name`,
    confirmPassword: `Confirm password`
  };

  public validationMessages: RtValidationMessagesFromForm<typeof this.form> = {
    username: {
      required: `User name is required`,
      maxlength: `User name should not be more than 200 characters`
    },
    email: {
      required: `Email is required`,
      email: `Email is not valid`,
      maxlength: `Email should not be more than 200 characters`
    },
    password: {
      required: `Password is required`,
      maxlength: `Password should not be more than 200 characters`
    },
    firstName: {
      required: `First name is required`,
      maxlength: `First name should not be more than 200 characters`
    },
    lastName: {
      required: `Last name is required`,
      maxlength: `Last name should not be more than 200 characters`
    },
    confirmPassword: {
      required: `Confirm password is required`,
      notMatch: `Passwords do not match`
    }
  };

  public placeholders: RtFormPlaceholdersFromForm<typeof this.form> = {
    username: 'Enter user name',
    email: 'Enter email',
    password: 'Enter password',
    firstName: 'Enter first name',
    lastName: 'Enter last name',
    confirmPassword: 'Confirm password'
  };

  protected constructor(protected store: Store, protected actions$: Actions, public authConfig: GpAuthConfigModel, protected token: string) {
  }

  ngOnInit() {
    this.defineFormControls();
    this.subscription.add(
      this.actions$.pipe(ofActionSuccessful(RegistrationSuccess, RegistrationFail))
        .subscribe({
          next: () => {
            this.form.enable();
            this.defineFormControls();
          }
        })
    );
  }

  onSubmit() {
    if (this.form.valid) {
      this.store.dispatch(new Registration(this.form.value as RegistrationData, this.token));
      this.form.disable();
    } else {
      this.form.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private defineFormControls() {
    switch (this.authConfig.authMethod) {
      case AUTH_METHOD.EMAIL:
        this.form.controls.username.disable();
        break;
      case AUTH_METHOD.USERNAME:
        this.form.controls.email.disable();
        break;
      case AUTH_METHOD.TELEGRAM_BOT:
        this.form.controls.email.disable();
        this.form.controls.username.disable();
        break;
    }
  }
}
